import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { View, Text, Image, TouchableOpacity, Modal, Pressable, Animated, StyleSheet, Platform, TextInput, Linking, ScrollView } from 'react-native';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import { getAuth, signOut } from 'firebase/auth';
import { getFirestore, collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import { Ionicons } from '@expo/vector-icons';
import { globalStyles } from '../styles/globalStyles';
import { useUser } from '../components/LoginPersistence';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { BREAKPOINTS } from '../styles/breakpoints';
import { HideOnScroll } from './HideOnScroll';
import { useCommunityModal } from '../contexts/CommunityModalContext';
import CommunityModal from './CommunityModal';
import { useTheme } from '../contexts/ThemeContext';
import { getResponsiveValue } from '../styles/breakpoints';
import { useTourPlannerNavVisible, useCommunityNavVisible, useCalendarNavVisible, useExpenseTrackerVisible, useNavLayout, useHideNavOnScroll, useUpdatedColorScheme, useTourCommunityHub, useCanvaHeaderLinks, useComingSoonUnlocks } from '../contexts/FeatureFlagsContext';
import { useAppLayout } from '../contexts/AppLayoutContext';
import { GradeVenueModal } from './GradeVenueModal';
import { PersonAvatar } from './PersonAvatar';
import { getPagePaletteIfEnabled, resolvePageScheme } from '../constants/pageColors';
import { CANVA_NAV_BLUE, CANVA_TITLE_CREAM, CANVA_MENU_BLUE, CANVA_TITLE_FONT, canvaHeaderChrome, NOTIFY_FEATURES, buildHamburgerMenuItems, isAdminUser, isValidNotifyEmail } from '../utils/hamburgerMenu';
import { submitFeatureNotify } from '../utils/featureNotifyService';

// ── Inject nav-icon hover animations once on web ────────────────────────────
if (Platform.OS === 'web' && typeof document !== 'undefined') {
  const NAV_ANIM_ID = 'vrc-nav-icon-anims';
  if (!document.getElementById(NAV_ANIM_ID)) {
    const s = document.createElement('style');
    s.id = NAV_ANIM_ID;
    s.textContent = `
      /* Base: all wrappers are flex + carry both transitions */
      .nav-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      /* Map — tilts like picking up a map · teal GPS glow */
      .nav-icon-map {
        transition: transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1),
                    filter  0.22s ease;
      }
      .nav-icon-map:hover {
        transform: rotate(-14deg) scale(1.14);
        filter: drop-shadow(0 0 7px #14b8a6);
      }

      /* Calendar — corner lifts, page about to flip · blue scheduling glow */
      .nav-icon-cal {
        transition: transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1),
                    filter  0.22s ease;
      }
      .nav-icon-cal:hover {
        transform: translateY(-4px) rotate(10deg) scale(1.1);
        filter: drop-shadow(0 0 7px #3b82f6);
      }

      /* Community — group spreads outward · warm social orange glow */
      .nav-icon-community {
        transition: transform 0.24s cubic-bezier(0.34, 1.56, 0.64, 1),
                    filter  0.2s ease;
      }
      .nav-icon-community:hover {
        transform: scale(1.22);
        filter: drop-shadow(0 0 8px #f97316);
      }

      /* Paper-plane — locked at takeoff angle · sky-blue flight glow */
      .nav-icon-plane {
        transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1),
                    filter  0.22s ease;
      }
      .nav-icon-plane:hover {
        transform: rotate(-38deg) translate(4px, -4px);
        filter: drop-shadow(0 0 7px #38bdf8);
      }

      /* Sun — quarter-turn, rays toward you · warm amber glow */
      .nav-icon-sun {
        transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
                    filter  0.25s ease;
      }
      .nav-icon-sun:hover {
        transform: rotate(45deg) scale(1.18);
        filter: drop-shadow(0 0 9px #f59e0b);
      }

      /* Moon — rising over the horizon · violet night glow */
      .nav-icon-moon {
        transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1),
                    filter  0.22s ease;
      }
      .nav-icon-moon:hover {
        transform: rotate(-28deg) scale(1.12);
        filter: drop-shadow(0 0 8px #a78bfa);
      }

      /* Dollar — coin flip bounce · green money glow */
      .nav-icon-dollar {
        transition: transform 0.32s cubic-bezier(0.34, 1.56, 0.64, 1),
                    filter  0.22s ease;
      }
      .nav-icon-dollar:hover {
        transform: translateY(-5px) scale(1.22);
        filter: drop-shadow(0 0 9px #03bb8d);
      }
    `;
    document.head.appendChild(s);
  }
}

// Dark mode toggle icons
const SunIcon = ({ size = 20, color = '#f1c40f' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="2">
    <circle cx="12" cy="12" r="5"/>
    <line x1="12" y1="1" x2="12" y2="3"/>
    <line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/>
    <line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

const MoonIcon = ({ size = 20, color = '#9b59b6' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

const NAV_BRAND_GREEN = CANVA_NAV_BLUE;
/** Phone/tablet header bar height (REQ-090, was 62). */
export const COMPACT_HEADER_HEIGHT = 72;
/** Space between the hamburger/avatar cluster and the light/dark toggle (REQ-090, was 8). */
export const MENU_THEME_TOGGLE_GAP = 22;
const NAV_TITLE_FONT = 'Atkinson Hyperlegible';
const CANVA_DONATE_URL = 'https://buymeacoffee.com/venuereportcard';

const USER_TYPES = {
  ADMIN: {
    label: 'Admin',
    value: 'admin',
    color: 'pink'
  },
  MEMBER: {
    label: 'Member',
    value: 'member',
    color: '#0bb5df'
  },
  VENUE_EMPLOYEE: {
    label: 'Venue Employee',
    value: 'venue_employee',
    color: '#2ecc71'
  },
  VENUE_OWNER: {
    label: 'Venue Owner',
    value: 'venue_owner',
    color: '#9b59b6'
  },
  ARTIST: {
    label: 'Artist',
    value: 'artist',
    color: '#e67e22'
  },
  BAND_MEMBER: {
    label: 'Band Member',
    value: 'band_member',
    color: '#f1c40f'
  },
  MUSIC_INDUSTRY: {
    label: 'Music Industry Employee',
    value: 'music_industry',
    color: '#1abc9c'
  }
};

function getActiveRouteName(state) {
  if (!state?.routes?.length) return '';
  const route = state.routes[state.index ?? 0];
  if (route.state) return getActiveRouteName(route.state);
  return route.name || '';
}

// After signing in from a menu prompt, land on the page the guest picked (Lauren 2026-10-29).
const AUTH_PROMPT_RETURN_ROUTES = { map: 'TourPlanner', directory: 'CommunityList' };
export function authPromptReturn(feature) {
  const returnTo = AUTH_PROMPT_RETURN_ROUTES[feature];
  return returnTo ? { returnTo } : {};
}

export function HeaderBar() {
  const { user, loading } = useUser();
  const { theme, isDarkMode, toggleTheme } = useTheme();
  const [menuVisible, setMenuVisible] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [notifyItemId, setNotifyItemId] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyStatus, setNotifyStatus] = useState('');
  const [notifySaving, setNotifySaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userName, setUserName] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  const navigation = useNavigation();
  const routeName = useNavigationState(getActiveRouteName) || '';
  const auth = getAuth();
  const db = getFirestore();
  const menuAnimation = useRef(new Animated.Value(0)).current;

  const mobileMenuAnimation = useRef(new Animated.Value(0)).current;
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= BREAKPOINTS.mobile : false
  );
  const [isTablet, setIsTablet] = useState(() =>
    typeof window !== 'undefined'
      ? window.innerWidth > BREAKPOINTS.mobile && window.innerWidth <= BREAKPOINTS.tablet
      : false
  );
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  const [mobileTitleFontSize, setMobileTitleFontSize] = useState(16);
  const mobileTitleFitRef = useRef(0);
  
  // Unread count and owned organizations (needed for header badge)
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [ownedOrganizations, setOwnedOrganizations] = useState([]);
  
  // Community Modal Context
  const {
    showModal: showActivityLog, // Renamed for existing usage
    setShowModal,
    activeTab,
    activeDiscussionTab,
    messagingTargetUserId,
    messagingExistingConversationId,
    openModal,
    openMessaging,
    closeModal,
    setActiveTab,
    setActiveDiscussionTab
  } = useCommunityModal();



  // Check if we're on mobile or tablet
  useEffect(() => {
    const checkScreenSize = () => {
      if (typeof window !== 'undefined') {
        const width = window.innerWidth;
        setWindowWidth(width);
        setIsMobile(width <= BREAKPOINTS.mobile);
        setIsTablet(width > BREAKPOINTS.mobile && width <= BREAKPOINTS.tablet);
      }
    };
    
    checkScreenSize();
    
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', checkScreenSize);
      return () => window.removeEventListener('resize', checkScreenSize);
    }
  }, []);

  useEffect(() => {
    if (menuVisible) {
      Animated.timing(menuAnimation, {
        toValue: 1,
        duration: 200,
        useNativeDriver: Platform.OS !== 'web',
      }).start();
    } else {
      Animated.timing(menuAnimation, {
        toValue: 0,
        duration: 200,
        useNativeDriver: Platform.OS !== 'web',
      }).start();
    }
  }, [menuVisible]);

  useEffect(() => {
    if (mobileMenuVisible) {
      Animated.timing(mobileMenuAnimation, {
        toValue: 1,
        duration: 300,
        useNativeDriver: Platform.OS !== 'web',
      }).start();
    } else {
      Animated.timing(mobileMenuAnimation, {
        toValue: 0,
        duration: 300,
        useNativeDriver: Platform.OS !== 'web',
      }).start();
    }
  }, [mobileMenuVisible]);

  // Single useEffect to handle user data
  useEffect(() => {
    if (!loading && user) {
      setIsAdmin(isAdminUser(user));
      setUserName(user.username || user.firstName || 'Guest');
      
      // Validate and set photoURL
      if (user.photoURL && typeof user.photoURL === 'string' && user.photoURL.startsWith('http')) {
        setPhotoURL(user.photoURL);
      } else {
        setPhotoURL('');
      }
    } else if (!loading) {
      setIsAdmin(false);
      setUserName('Guest');
      setPhotoURL('');
    }
  }, [user, loading]);

  // Load organizations owned by the user (needed for unread count calculation)
  const loadOwnedOrganizations = useCallback(async () => {
    if (!user?.uid) {
      setOwnedOrganizations([]);
      return;
    }
    try {
      const orgsQuery = query(
        collection(db, 'organizations'),
        where('ownerId', '==', user.uid)
      );
      const snapshot = await getDocs(orgsQuery);
      const orgs = [];
      snapshot.forEach(doc => {
        orgs.push({ id: doc.id, ...doc.data() });
      });
      setOwnedOrganizations(orgs);
    } catch (error) {
      console.error('Error loading owned organizations:', error);
      setOwnedOrganizations([]);
    }
  }, [user?.uid, db]);

  useEffect(() => {
    loadOwnedOrganizations();
  }, [loadOwnedOrganizations]);

  // Listen for unread message counts in real-time
  // Calculate unread for both user and all owned organizations
  // Also include unanswered organization invitations
  useEffect(() => {
    if (!user?.uid || !user?.email) {
      setTotalUnreadCount(0);
      return;
    }

    // Get all IDs to check unread counts for (user + all owned organizations)
    const allSenderIds = [user.uid, ...ownedOrganizations.map(org => org.id)];
    
    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains-any', allSenderIds)
    );

    // Query for pending organization invitations for this user
    const invitationsQuery = query(
      collection(db, 'organization_invitations'),
      where('inviteeEmail', '==', user.email),
      where('status', '==', 'pending')
    );

    let conversationUnread = 0;
    let invitationCount = 0;
    let tourInviteCount = 0;

    const emailLower = String(user.email || '').trim().toLowerCase();
    const tourInvitesQuery = query(
      collection(db, 'tourInvites'),
      where('inviteeEmail', '==', emailLower),
      where('status', '==', 'pending')
    );

    const unsubscribeConversations = onSnapshot(conversationsQuery, (snapshot) => {
      conversationUnread = 0;
      
      snapshot.docs.forEach(doc => {
        const conversationData = doc.data();
        // Sum unread counts for user and all owned organizations
        allSenderIds.forEach(senderId => {
          const unreadCount = conversationData.unreadCounts?.[senderId] || 0;
          conversationUnread += unreadCount;
        });
      });
      
      // Update total with both conversation unread and invitation count
      setTotalUnreadCount(conversationUnread + invitationCount + tourInviteCount);
    }, (error) => {
      console.error('🔔 Error listening to conversations:', error);
      setTotalUnreadCount(invitationCount + tourInviteCount);
    });

    const unsubscribeInvitations = onSnapshot(invitationsQuery, (snapshot) => {
      invitationCount = snapshot.size;
      
      // Update total with both conversation unread and invitation count
      setTotalUnreadCount(conversationUnread + invitationCount + tourInviteCount);
    }, (error) => {
      console.error('🔔 Error listening to organization invitations:', error);
      setTotalUnreadCount(conversationUnread + tourInviteCount);
    });

    const unsubscribeTourInvites = onSnapshot(tourInvitesQuery, (snapshot) => {
      tourInviteCount = snapshot.size;
      setTotalUnreadCount(conversationUnread + invitationCount + tourInviteCount);
    }, (error) => {
      console.error('🔔 Error listening to tour invitations:', error);
      setTotalUnreadCount(conversationUnread + invitationCount);
    });

    return () => {
      unsubscribeConversations();
      unsubscribeInvitations();
      unsubscribeTourInvites();
    };
  }, [user?.uid, user?.email, ownedOrganizations]);

  // Format unread count for display
  const formatUnreadCount = (count) => {
    if (count === 0) return null;
    if (count >= 100) return '99+';
    return count.toString();
  };

  const handleSignOut = async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        localStorage.removeItem('devModeUser');
      }
      await signOut(auth);
      navigation.replace('Home');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleLoginClick = () => {
    navigation.navigate('Login');
  };

  const handleSignUpClick = () => {
    navigation.navigate('Login', { isSignUp: true });
  };

  const isDevelopment = process.env.NODE_ENV === 'development';
  const showTourPlannerNav = useTourPlannerNavVisible(user);
  const showCommunityNav = useCommunityNavVisible(user);
  const showCalendarNav = useCalendarNavVisible(user);
  const showExpenseTracker = useExpenseTrackerVisible(user);
  const comingSoonUnlocks = useComingSoonUnlocks(user);
  const showTourCommunityHub = useTourCommunityHub();
  const showCanvaHeaderLinks = useCanvaHeaderLinks();
  const [gradeModalVisible, setGradeModalVisible] = useState(false);
  const { ready: navLayoutReady, showLeftLogoNav, showSimpleCenterNav, showStackedCenterNav, showCenteredNav, showGridNav } = useNavLayout();
  const hideNavOnScroll = useHideNavOnScroll();
  const updatedColorScheme = useUpdatedColorScheme();
  const { setHeaderHeight, setMobileNavContentWidth, pageSchemeOverride, toggleExpenseTracker } = useAppLayout();
  const [communityAuthPromptVisible, setCommunityAuthPromptVisible] = useState(false);
  const [authPromptFeature, setAuthPromptFeature] = useState('community');
  const [stackedTitleWidth, setStackedTitleWidth] = useState(0);

  const STACKED_NAV_GAP = isMobile ? 4 : 6;
  const isCompactHeader = isMobile || isTablet;
  const MOBILE_HEADER_SIDE_SLOT = 44;
  const MOBILE_HEADER_PADDING = 12;

  const compactHeaderCenterWidth = useMemo(() => {
    if (!isCompactHeader) return 0;
    return Math.max(200, windowWidth - 2 * MOBILE_HEADER_PADDING - 2 * MOBILE_HEADER_SIDE_SLOT);
  }, [isCompactHeader, windowWidth]);

  const stackedNavButtonCount = useMemo(() => {
    let count = 2;
    // if (showTourPlannerNav) count += 1;
    if (showCalendarNav) count += 1;
    if (showCommunityNav) count += 1;
    if (showExpenseTracker) count += 1;
    count += 1; // Messages shows for guests too
    return count;
  }, [showTourPlannerNav, showCalendarNav, showCommunityNav, user, showExpenseTracker]);

  const stackedNavFullButtonCount = 6;
  const useFullNavRowLayout = stackedNavButtonCount >= stackedNavFullButtonCount;

  const stackedBtnSize = useMemo(() => {
    if (!useFullNavRowLayout) {
      return isCompactHeader ? 38 : 46;
    }
    if (isCompactHeader) {
      if (stackedNavButtonCount < 1) return 34;
      const availableCenterWidth = compactHeaderCenterWidth;
      const gaps = (stackedNavButtonCount - 1) * STACKED_NAV_GAP;
      const computed = (availableCenterWidth - gaps) / stackedNavButtonCount;
      const minSize = 28;
      const maxSize = 42;
      return Math.min(maxSize, Math.max(minSize, Math.floor(computed)));
    }
    if (!stackedTitleWidth || stackedNavButtonCount < 1) {
      return 46;
    }
    const gaps = (stackedNavButtonCount - 1) * STACKED_NAV_GAP;
    const computed = (stackedTitleWidth - gaps) / stackedNavButtonCount;
    const minSize = 42;
    const maxSize = 58;
    return Math.min(maxSize, Math.max(minSize, Math.floor(computed)));
  }, [stackedTitleWidth, stackedNavButtonCount, isCompactHeader, STACKED_NAV_GAP, compactHeaderCenterWidth, useFullNavRowLayout]);

  const stackedNavRowWidth = useMemo(() => {
    if (stackedNavButtonCount < 1 || !stackedBtnSize) return 0;
    return stackedNavButtonCount * stackedBtnSize + (stackedNavButtonCount - 1) * STACKED_NAV_GAP;
  }, [stackedNavButtonCount, stackedBtnSize, STACKED_NAV_GAP]);

  useEffect(() => {
    if (!isCompactHeader) return;
    mobileTitleFitRef.current = 0;
    if (!useFullNavRowLayout) {
      setMobileTitleFontSize(16);
      return;
    }
    setMobileTitleFontSize(16);
  }, [stackedNavRowWidth, isCompactHeader, useFullNavRowLayout]);

  const handleMobileTitleLayout = useCallback((textWidth) => {
    if (!isCompactHeader || !useFullNavRowLayout || stackedNavRowWidth <= 0 || textWidth <= 0) return;
    if (mobileTitleFitRef.current > 14) return;
    const diff = stackedNavRowWidth - textWidth;
    if (Math.abs(diff) <= 1.5) return;
    mobileTitleFitRef.current += 1;
    setMobileTitleFontSize((prev) => {
      const ratio = stackedNavRowWidth / textWidth;
      return Math.min(24, Math.max(10, prev * ratio));
    });
  }, [isCompactHeader, stackedNavRowWidth, useFullNavRowLayout]);

  useEffect(() => {
    if (showStackedCenterNav && isCompactHeader && useFullNavRowLayout && stackedNavRowWidth > 0) {
      setMobileNavContentWidth(stackedNavRowWidth);
      return;
    }
    setMobileNavContentWidth(0);
  }, [showStackedCenterNav, isCompactHeader, useFullNavRowLayout, stackedNavRowWidth, setMobileNavContentWidth]);

  const stackedIconSize = Math.max(isCompactHeader ? 16 : 22, Math.round(stackedBtnSize * 0.58));

  const stackedProfileSize = isMobile ? 40 : 44;

  const handleCommunityPress = () => {
    navigation.navigate('CommunityScreen');
  };

  const liveMenuActions = {
    venues: () => navigation.navigate('VenueList'),
    search: () => navigation.navigate('VenueList'),
    add: () => navigation.navigate('AddVenue'),
    grade: () => setGradeModalVisible(true),
    admin: () => navigation.navigate('Admin'),
    account: () => navigation.navigate('Profile'),
    fund: () => navigation.navigate('MutualAidFund'),
    balance: () => navigation.navigate('Wallet'),
    calendar: () => navigation.navigate('MyCalendar'),
    map: () => navigation.navigate('TourPlanner'),
    directory: () => navigation.navigate('CommunityList'),
    gear: () => navigation.navigate('GearSwapList'),
    events: () => navigation.navigate('EventsList'),
    budget: () => openBudget(),
  };

  // Guests still see the Budget icon; tapping it asks them to sign in (Budget is for signed-up users).
  // Guests see the Messages (paper plane) button too; tapping it asks them to sign in.
  const openMessages = () => {
    if (!user) {
      setAuthPromptFeature('messages');
      setCommunityAuthPromptVisible(true);
      return;
    }
    if (showActivityLog) closeModal(); else openModal();
  };

  const openBudget = () => {
    if (!user) {
      setAuthPromptFeature('budget');
      setCommunityAuthPromptVisible(true);
      return;
    }
    toggleExpenseTracker();
  };

  const openNotifyFeature = (id) => {
    const meta = NOTIFY_FEATURES[id];
    if (!meta) return;
    if (meta.routeName === 'NotifyWhenLive') {
      navigation.navigate('NotifyWhenLive', { featureId: id });
      return;
    }
    navigation.navigate(meta.routeName);
  };

  const menuItems = buildHamburgerMenuItems({
    user,
    showTourCommunityHub,
    showCommunityNav,
    showExpenseTracker,
    showCalendarNav,
    showTourPlannerNav,
    isAdmin,
    isDevelopment,
    unlockedFeatures: comingSoonUnlocks,
  }).map((item) => ({
    ...item,
    onPress: liveMenuActions[item.id] || null,
  }));

  const submitNotifyForItem = async (item) => {
    if (notifySaving) return;
    const fallback = String(user?.email || '').trim();
    const email = notifyEmail.trim() || fallback;
    if (!isValidNotifyEmail(email)) {
      setNotifyStatus('Enter a valid email address.');
      return;
    }
    setNotifySaving(true);
    setNotifyStatus('');
    try {
      await submitFeatureNotify(db, {
        email,
        feature: item.title,
        uid: user?.uid || null,
      });
      setNotifyStatus(`We’ll email you when ${item.title} is live.`);
      setNotifyEmail('');
    } catch (err) {
      setNotifyStatus(err?.message || 'Could not save that. Try again.');
    } finally {
      setNotifySaving(false);
    }
  };

  const handleMenuItemPress = (item) => {
    if (item.signInRequired) {
      setMenuVisible(false);
      setMobileMenuVisible(false);
      setNotifyItemId(null);
      setAuthPromptFeature(item.id);
      setCommunityAuthPromptVisible(true);
      return;
    }
    if (item.live && item.onPress) {
      setMenuVisible(false);
      setMobileMenuVisible(false);
      setNotifyItemId(null);
      item.onPress();
      return;
    }
    setMenuVisible(false);
    setMobileMenuVisible(false);
    setNotifyItemId(null);
    openNotifyFeature(item.id);
  };
  const profileDisplayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || userName;

  // Add hover effect to menu items
  const menuItemHoverStyle = {
    color: user ? (USER_TYPES[user?.userType?.toUpperCase()]?.color || '#0bb5df') : '#0bb5df',
  };

  const activePageScheme = resolvePageScheme(routeName, pageSchemeOverride);
  const pagePalette = getPagePaletteIfEnabled(activePageScheme, isDarkMode, updatedColorScheme);

  const useCanvaHeader = showLeftLogoNav || !pagePalette;
  const canvaChrome = canvaHeaderChrome(isDarkMode);
  const navBg = useCanvaHeader
    ? canvaChrome.bar
    : pagePalette.headerBackground;
  const navBorder = useCanvaHeader
    ? (isDarkMode ? 'rgba(17,17,17,0.18)' : 'rgba(255,255,255,0.18)')
    : (isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)');
  const iconColor = useCanvaHeader
    ? canvaChrome.icon
    : pagePalette && !isDarkMode && activePageScheme === 'gearSwap'
      ? '#333'
      : pagePalette && activePageScheme === 'calendar' && !isDarkMode
        ? '#333'
        : isDarkMode
          ? '#e0e0e0'
          : '#333';
  const navTitleColor = useCanvaHeader ? canvaChrome.title : (pagePalette?.headerTitle ?? canvaChrome.title);
  const navTitleFont = useCanvaHeader ? CANVA_TITLE_FONT : NAV_TITLE_FONT;
  const navWelcomeColor = useCanvaHeader ? '#ffffff' : (pagePalette?.headerWelcome ?? '#0bb5df');
  const useSolidHeader = Boolean(pagePalette) || useCanvaHeader;

  // REQ-090: taller phone/tablet bar so the hamburger, title and messages icon don't crowd (was 62).
  const headerHeight = isCompactHeader
    ? COMPACT_HEADER_HEIGHT
    : showLeftLogoNav
      ? 100
      : showSimpleCenterNav
        ? 100
        : (showStackedCenterNav ? 118 : showGridNav ? 106 : (showCenteredNav ? 76 : 72));

  useEffect(() => {
    setHeaderHeight(headerHeight);
  }, [headerHeight, setHeaderHeight]);

  const renderCanvaChromeLinks = () => {
    if (!showCanvaHeaderLinks) return null;
    const linkStyle = {
      color: navTitleColor,
      fontFamily: NAV_TITLE_FONT,
      fontSize: 11,
      fontWeight: '700',
      letterSpacing: 0.8,
      textTransform: 'uppercase',
    };
    return (
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
        <TouchableOpacity onPress={() => navigation.navigate('AboutUs')} accessibilityLabel="About">
          <Text style={linkStyle}>About</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => navigation.navigate('Contact')} accessibilityLabel="Contact">
          <Text style={linkStyle}>Contact</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => Linking.openURL(CANVA_DONATE_URL)} accessibilityLabel="Donate">
          <Text style={linkStyle}>Donate</Text>
        </TouchableOpacity>
      </View>
    );
  };

  const renderSimpleCenterHeader = () => {
    const titleBlock = (
      <Pressable onPress={() => navigation.navigate('Home')}>
        <Text
          style={{
            fontFamily: navTitleFont,
            fontSize: isMobile ? 13 : 28,
            fontWeight: '400',
            letterSpacing: isMobile ? 1.5 : 4,
            textTransform: 'uppercase',
            color: navTitleColor,
            textAlign: 'center',
          }}
          numberOfLines={1}
        >
          VENUE REPORT CARD
        </Text>
      </Pressable>
    );

    if (!isCompactHeader) {
      return (
        <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', position: 'relative' }}>
          <View
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'box-none',
              zIndex: 1,
            }}
          >
            {titleBlock}
          </View>
          {showCanvaHeaderLinks ? (
            <View style={{ zIndex: 2, alignSelf: 'center' }}>
              {renderCanvaChromeLinks()}
            </View>
          ) : null}
          <View style={{ flex: 1 }} />
          <View style={{ zIndex: 2, alignSelf: 'flex-start' }}>
            <StackedCenterProfile />
          </View>
        </View>
      );
    }

    return (
      <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
        <View style={{ width: MOBILE_HEADER_SIDE_SLOT, flexShrink: 0, alignItems: 'flex-start', justifyContent: 'center' }}>
          <HamburgerIcon />
        </View>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', minWidth: 0 }}>
          {titleBlock}
        </View>
        <View style={{ width: MOBILE_HEADER_SIDE_SLOT, flexShrink: 0 }} />
      </View>
    );
  };

  const renderStackedCenterBlock = () => (
    <View style={{ alignItems: useFullNavRowLayout ? 'stretch' : 'center', pointerEvents: 'box-none' }}>
      <Pressable
        onPress={() => navigation.navigate('Home')}
        style={{
          alignSelf: 'center',
          ...(useFullNavRowLayout && isCompactHeader && stackedNavRowWidth > 0 ? { width: stackedNavRowWidth } : {}),
        }}
      >
        <Text
          onLayout={(event) => {
            const width = event.nativeEvent.layout.width;
            if (isCompactHeader && useFullNavRowLayout) {
              handleMobileTitleLayout(width);
            } else if (!isCompactHeader && useFullNavRowLayout) {
              setStackedTitleWidth(width);
            }
          }}
          style={{
            fontFamily: navTitleFont,
            fontSize: isCompactHeader ? mobileTitleFontSize : 30,
            fontWeight: '400',
            letterSpacing: isCompactHeader ? 1.5 : 4,
            textTransform: 'uppercase',
            color: navTitleColor,
            textAlign: 'center',
          }}
          numberOfLines={1}
        >
          VENUE REPORT CARD
        </Text>
      </Pressable>
      {!isCompactHeader && showCanvaHeaderLinks ? (
        <View style={{ marginTop: 4, alignSelf: 'center' }}>{renderCanvaChromeLinks()}</View>
      ) : null}
      {isCompactHeader ? null : (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: STACKED_NAV_GAP,
          marginTop: 6,
          width: useFullNavRowLayout
            ? (stackedTitleWidth > 0 ? stackedTitleWidth : undefined)
            : (stackedNavRowWidth > 0 ? stackedNavRowWidth : undefined),
          alignSelf: 'center',
          justifyContent: useFullNavRowLayout ? 'flex-start' : 'center',
        }}
      >
        <GridNavBtn onPress={() => navigation.navigate('TourPlanner')} label="Tour Planner" size={stackedBtnSize}>
          <Image source={require('../../assets/icons/nav-map.png')} style={{ width: stackedIconSize, height: stackedIconSize }} resizeMode="contain" />
        </GridNavBtn>
        {showCalendarNav && (
          <GridNavBtn onPress={() => navigation.navigate('MyCalendar')} label="Calendar" size={stackedBtnSize}>
            <Image source={require('../../assets/icons/nav-calendar.png')} style={{ width: stackedIconSize, height: stackedIconSize }} resizeMode="contain" />
          </GridNavBtn>
        )}
        <GridNavBtn onPress={() => navigation.navigate('Home')} label="Home" size={stackedBtnSize}>
          <Image source={require('../../assets/logo.png')} style={{ width: stackedIconSize, height: stackedIconSize }} resizeMode="contain" />
        </GridNavBtn>
        {showCommunityNav && (
          <GridNavBtn onPress={handleCommunityPress} label="Community" size={stackedBtnSize}>
            <Image
              source={communityIconSource}
              style={{ width: stackedIconSize, height: stackedIconSize }}
              resizeMode="contain"
            />
          </GridNavBtn>
        )}
        {showExpenseTracker && (
          <GridNavBtn onPress={() => openBudget()} label="Expenses" size={stackedBtnSize}>
            <Image source={require('../../assets/icons/nav-wallet.png')} style={{ width: stackedIconSize, height: stackedIconSize }} resizeMode="contain" />
          </GridNavBtn>
        )}
        {(
          <GridNavBtn
            onPress={() => openMessages()}
            label="Messages"
            badge={formatUnreadCount(totalUnreadCount)}
            size={stackedBtnSize}
          >
            <Image source={require('../../assets/icons/nav-plane.png')} style={{ width: stackedIconSize, height: stackedIconSize }} resizeMode="contain" />
          </GridNavBtn>
        )}
      </View>
      )}
    </View>
  );

  // Avoid flashing centered nav while Firestore flags load on first visit.
  if (!navLayoutReady) {
    return (
      <HideOnScroll enabled={hideNavOnScroll} style={[globalStyles.headerContainer, {
        backgroundColor: navBg,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottomWidth: 1,
        borderBottomColor: navBorder,
        height: isMobile ? 96 : 108,
        paddingHorizontal: isMobile ? 12 : 24,
        opacity: 0,
      }]}>
        <View style={{ flex: 1 }} />
      </HideOnScroll>
    );
  }

  // Hamburger menu icon component
  const HamburgerIcon = () => (
    <TouchableOpacity 
      style={[
        globalStyles.hamburgerButton,
        useCanvaHeader ? { backgroundColor: 'rgba(228,227,227,0.12)' } : null,
      ]}
      onPress={() => setMobileMenuVisible(true)}
      accessibilityLabel="Open menu"
    >
      <View style={[globalStyles.hamburgerLine, useCanvaHeader && { backgroundColor: CANVA_TITLE_CREAM }]} />
      <View style={[globalStyles.hamburgerLine, useCanvaHeader && { backgroundColor: CANVA_TITLE_CREAM }]} />
      <View style={[globalStyles.hamburgerLine, useCanvaHeader && { backgroundColor: CANVA_TITLE_CREAM }]} />
    </TouchableOpacity>
  );

  const renderCompactNavHeader = (titleNode) => (
    <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
      <View style={{ width: MOBILE_HEADER_SIDE_SLOT, flexShrink: 0, alignItems: 'flex-start', justifyContent: 'center' }}>
        <HamburgerIcon />
      </View>
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', minWidth: 0 }}>
        {titleNode}
      </View>
      <View style={{ width: MOBILE_HEADER_SIDE_SLOT, flexShrink: 0, alignItems: 'flex-end', justifyContent: 'center' }}>
        {(
          <NavBtn
            onPress={() => openMessages()}
            label="Messages"
            badge={formatUnreadCount(totalUnreadCount)}
            animClass="nav-icon-plane"
          >
            <Ionicons name="paper-plane-outline" size={22} color={iconColor} />
          </NavBtn>
        )}
      </View>
    </View>
  );

  // ── Shared icon button ──────────────────────────────────────────────────────
  // animClass: one of 'nav-icon-map' | 'nav-icon-cal' | 'nav-icon-community' |
  //            'nav-icon-plane' | 'nav-icon-sun' | 'nav-icon-moon' | 'nav-icon-dollar'
  const NavBtn = ({ onPress, children, label, badge, animClass, size }) => (
    <TouchableOpacity
      onPress={onPress}
      accessibilityLabel={label}
      style={[navBtnStyle(isDarkMode), size ? { width: size, height: size } : null]}
    >
      {(Platform.OS === 'web' && animClass) ? (
        <div className={`nav-icon ${animClass}`}>
          {children}
        </div>
      ) : children}
      {badge ? (
        <View style={headerUnreadBadgeStyle}>
          <Text style={headerUnreadTextStyle}>{badge}</Text>
        </View>
      ) : null}
    </TouchableOpacity>
  );

  // ── Grid nav icon button (no hover animations, green outline, colored icons) ─
  const GridNavBtn = ({ onPress, children, label, badge: gridBadge, size }) => {
    const btnSize = size ?? (isMobile ? 36 : 44);
    return (
      <TouchableOpacity
        onPress={onPress}
        accessibilityLabel={label}
        style={{
          position: 'relative',
          width: btnSize,
          height: btnSize,
          borderRadius: 0,
          backgroundColor: 'transparent',
          borderWidth: 1.5,
          borderColor: NAV_BRAND_GREEN,
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        {children}
        {gridBadge ? (
          <View style={headerUnreadBadgeStyle}>
            <Text style={headerUnreadTextStyle}>{gridBadge}</Text>
          </View>
        ) : null}
      </TouchableOpacity>
    );
  };

  // Community: -light asset in light mode, regular icon in dark mode.
  const communityIconSource = isDarkMode
    ? require('../../assets/icons/nav-community.png')
    : require('../../assets/icons/nav-community-light.png');

  const ThemeToggleButton = ({ style, animated = true }) => (
    <TouchableOpacity
      onPress={toggleTheme}
      style={[
        darkModeToggleStyle,
        isDarkMode && {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderWidth: 1,
          borderColor: 'rgba(255, 255, 255, 0.18)',
        },
        style,
      ]}
      accessibilityLabel={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {animated && Platform.OS === 'web' ? (
        <div className={`nav-icon ${isDarkMode ? 'nav-icon-sun' : 'nav-icon-moon'}`}>
          <Ionicons
            name={isDarkMode ? 'sunny-outline' : 'moon-outline'}
            size={18}
            color={isDarkMode ? '#f1c40f' : '#9b59b6'}
          />
        </div>
      ) : (
        <Ionicons
          name={isDarkMode ? 'sunny-outline' : 'moon-outline'}
          size={18}
          color={isDarkMode ? '#f1c40f' : '#9b59b6'}
        />
      )}
    </TouchableOpacity>
  );

  const StackedCenterProfile = () => {
    const isStackedDesktop = (showStackedCenterNav || showSimpleCenterNav || showLeftLogoNav) && !isMobile && !isTablet;

    const welcomeTextStyle = {
      fontFamily: 'VastagoGrotesk',
      fontSize: 15,
      fontWeight: '600',
      color: navWelcomeColor,
      textTransform: 'lowercase',
      letterSpacing: 0.2,
    };

    const renderProfileAvatar = (size) => (
      <View
        style={[
          globalStyles.headerProfile,
          size
            ? {
                width: size,
                height: size,
                borderRadius: size / 2,
              }
            : null,
        ]}
      >
        <TouchableOpacity
          onPress={() => setMenuVisible(true)}
          onMouseEnter={() => setMenuVisible(true)}
          style={{ width: '100%', height: '100%' }}
        >
          <PersonAvatar
            photoURL={photoURL}
            name={profileDisplayName}
            email={user?.email}
            uid={user?.uid}
            size={40}
            style={globalStyles.headerProfileImage}
          />
        </TouchableOpacity>
        {user?.verificationStatus === 'approved' && (
          <VerifiedBadge style={[globalStyles.verifiedBadge, { backgroundColor: USER_TYPES[user.userType?.toUpperCase()]?.color || '#0bb5df' }]} />
        )}
      </View>
    );

    if (showLeftLogoNav) {
      // REQ-090: wide gap so the menu trigger (hover opens the dropdown) isn't on top of the moon toggle.
      return (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: MENU_THEME_TOGGLE_GAP }}>
          {user ? (
            renderProfileAvatar(!isCompactHeader ? stackedProfileSize : null)
          ) : (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <HamburgerIcon />
              <TouchableOpacity style={globalStyles.loginButton} onPress={handleLoginClick}>
                <Text style={globalStyles.loginButtonText}>Login</Text>
              </TouchableOpacity>
              {!isMobile && (
                <TouchableOpacity style={globalStyles.signupButton} onPress={handleSignUpClick}>
                  <Text style={globalStyles.signupButtonText}>Sign Up</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
          <ThemeToggleButton style={{ marginRight: 0 }} />
        </View>
      );
    }

    return (
    <View
      style={{
        alignItems: 'flex-end',
        justifyContent: 'flex-start',
      }}
    >
      <View style={{ justifyContent: 'flex-start' }}>
        {user ? (
          renderProfileAvatar(isStackedDesktop ? stackedProfileSize : null)
        ) : (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: MENU_THEME_TOGGLE_GAP }}>
            <ThemeToggleButton style={{ marginRight: 0, flexShrink: 0 }} />
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <HamburgerIcon />
              <TouchableOpacity style={globalStyles.loginButton} onPress={handleLoginClick}>
                <Text style={globalStyles.loginButtonText}>Login</Text>
              </TouchableOpacity>
              <TouchableOpacity style={globalStyles.signupButton} onPress={handleSignUpClick}>
                <Text style={globalStyles.signupButtonText}>Sign Up</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </View>

      {user && !isMobile && (
        showSimpleCenterNav ? (
          <View style={{ marginTop: 4, position: 'relative', alignSelf: 'flex-end' }}>
            <Text style={welcomeTextStyle} numberOfLines={1}>
              welcome {userName || 'guest'}
            </Text>
            <View
              style={{
                position: 'absolute',
                left: 0,
                top: -40,
              }}
            >
              <ThemeToggleButton style={{ marginRight: 0 }} />
            </View>
          </View>
        ) : (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              marginTop: 4,
              width: isStackedDesktop ? 240 : 180,
            }}
          >
            <ThemeToggleButton style={{ marginRight: 0 }} />
            <Text style={welcomeTextStyle} numberOfLines={1}>
              welcome {userName || 'guest'}
            </Text>
          </View>
        )
      )}
    </View>
    );
  };

  const renderLeftLogoHeader = () => {
    const iconPx = 22;
    const cell = 40;
    const gap = 6;
    const titleNode = (
      <Pressable onPress={() => navigation.navigate('Home')}>
        <Text
          numberOfLines={1}
          style={{
            fontFamily: CANVA_TITLE_FONT,
            fontSize: isCompactHeader ? (windowWidth < 360 ? 17 : 20) : 44,
            fontWeight: '400',
            color: navTitleColor,
            textAlign: 'center',
            ...(Platform.OS === 'web'
              ? {
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                }
              : {}),
          }}
        >
          VENUE REPORT CARD
        </Text>
      </Pressable>
    );

    if (isCompactHeader) {
      return renderCompactNavHeader(titleNode);
    }

    return (
      <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', minWidth: 0, position: 'relative' }}>
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            width: 3 * cell + 2 * gap,
            gap,
            alignItems: 'center',
            flexShrink: 0,
            zIndex: 2,
          }}
        >
          <NavBtn onPress={() => navigation.navigate('Home')} label="Home">
            <Image source={require('../../assets/logo.png')} style={{ width: 36, height: 36 }} resizeMode="contain" />
          </NavBtn>
          {showCommunityNav ? (
            <NavBtn onPress={handleCommunityPress} label="Community" animClass="nav-icon-community">
              <Ionicons name="people-outline" size={iconPx} color={iconColor} />
            </NavBtn>
          ) : null}
          {(
            <NavBtn
              onPress={() => openMessages()}
              label="Messages"
              badge={formatUnreadCount(totalUnreadCount)}
              animClass="nav-icon-plane"
            >
              <Ionicons name="paper-plane-outline" size={iconPx} color={iconColor} />
            </NavBtn>
          )}
          <NavBtn onPress={() => navigation.navigate('TourPlanner')} label="Map" animClass="nav-icon-map">
            <Ionicons name="map-outline" size={iconPx} color={iconColor} />
          </NavBtn>
          {showCalendarNav ? (
            <NavBtn onPress={() => navigation.navigate('MyCalendar')} label="Calendar" animClass="nav-icon-cal">
              <Ionicons name="calendar-outline" size={iconPx} color={iconColor} />
            </NavBtn>
          ) : null}
          {showExpenseTracker ? (
            <NavBtn onPress={() => openBudget()} label="Expense Tracker" animClass="nav-icon-dollar">
              <Ionicons name="wallet-outline" size={iconPx} color={iconColor} />
            </NavBtn>
          ) : null}
        </View>

        <View
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            pointerEvents: 'box-none',
          }}
        >
          {titleNode}
          {!isCompactHeader && showCanvaHeaderLinks ? (
            <View style={{ marginTop: 6 }}>{renderCanvaChromeLinks()}</View>
          ) : null}
        </View>

        <View style={{ flex: 1 }} />

        <View style={{ flexShrink: 0, zIndex: 2 }}>
          <StackedCenterProfile />
        </View>
      </View>
    );
  };

  return (
    <HideOnScroll enabled={hideNavOnScroll} style={[globalStyles.headerContainer, {
      backgroundColor: navBg,
      ...(useSolidHeader ? {} : {
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      }),
      borderBottomWidth: 1,
      borderBottomColor: navBorder,
      boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
      height: headerHeight,
      paddingHorizontal: isCompactHeader ? MOBILE_HEADER_PADDING : 24,
      paddingVertical: isCompactHeader ? 8 : 10,
    }]}>

      {/* ── LEFT LOGO NAV (logo left, icon grid, large title) ─ */}
      {showLeftLogoNav ? (
        renderLeftLogoHeader()
      ) : showSimpleCenterNav ? (
        renderSimpleCenterHeader()
      ) : showStackedCenterNav ? (
        isCompactHeader ? (
          renderCompactNavHeader(renderStackedCenterBlock())
        ) : (
          <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', position: 'relative' }}>
            <View
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'box-none',
                zIndex: 1,
              }}
            >
              {renderStackedCenterBlock()}
            </View>
            <View style={{ flex: 1 }} />
            <View style={{ zIndex: 2, alignSelf: 'flex-start' }}>
              <StackedCenterProfile />
            </View>
          </View>
        )
      ) : showGridNav && isCompactHeader ? (
          renderCompactNavHeader(
            <Pressable onPress={() => navigation.navigate('Home')}>
              <Text
                numberOfLines={1}
                style={{
                  fontFamily: navTitleFont,
                  fontSize: 18,
                  fontWeight: '400',
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  color: navTitleColor,
                  textAlign: 'center',
                }}
              >
                VENUE REPORT CARD
              </Text>
            </Pressable>
          )
      ) : showGridNav ? (
        <>
          {/* Left: 2×3 grid of icon buttons — custom image icons */}
          <View style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            width: isMobile ? (3 * 36 + 2 * 5) : (3 * 44 + 2 * 6),
            gap: isMobile ? 5 : 6,
            alignContent: 'center',
          }}>
            {/* Row 1: Logo (home), Calendar, Paper plane */}
            <GridNavBtn onPress={() => navigation.navigate('Home')} label="Home">
              <Image source={require('../../assets/logo.png')} style={{ width: isMobile ? 22 : 28, height: isMobile ? 22 : 28 }} resizeMode="contain" />
            </GridNavBtn>
            <GridNavBtn onPress={() => navigation.navigate('MyCalendar')} label="Calendar">
              <Image source={require('../../assets/icons/nav-calendar.png')} style={{ width: isMobile ? 22 : 28, height: isMobile ? 22 : 28 }} resizeMode="contain" />
            </GridNavBtn>
            {(
              <GridNavBtn
                onPress={() => openMessages()}
                label="Messages"
                badge={formatUnreadCount(totalUnreadCount)}
              >
                <Image source={require('../../assets/icons/nav-plane.png')} style={{ width: isMobile ? 22 : 28, height: isMobile ? 22 : 28 }} resizeMode="contain" />
              </GridNavBtn>
            )}
            {/* Row 2: Community, Map, Wallet */}
              {showCommunityNav && (
                <GridNavBtn onPress={handleCommunityPress} label="Community">
                  <Image
                    source={communityIconSource}
                    style={{ width: isMobile ? 22 : 28, height: isMobile ? 22 : 28 }}
                    resizeMode="contain"
                  />
                </GridNavBtn>
              )}
            <GridNavBtn onPress={() => navigation.navigate('TourPlanner')} label="Tour Planner">
              <Image source={require('../../assets/icons/nav-map.png')} style={{ width: isMobile ? 22 : 28, height: isMobile ? 22 : 28 }} resizeMode="contain" />
            </GridNavBtn>
            {showExpenseTracker && (
              <GridNavBtn onPress={() => openBudget()} label="Expenses">
                <Image source={require('../../assets/icons/nav-wallet.png')} style={{ width: isMobile ? 22 : 28, height: isMobile ? 22 : 28 }} resizeMode="contain" />
              </GridNavBtn>
            )}
          </View>

          {/* Center: VENUE REPORT CARD title */}
          <Pressable onPress={() => navigation.navigate('Home')} style={{ alignItems: 'center', flex: 1 }}>
            <Text style={{
              fontFamily: navTitleFont,
              fontSize: isMobile ? 11 : 30,
              fontWeight: '400',
              letterSpacing: isMobile ? 2 : 4,
              textTransform: 'uppercase',
              color: navTitleColor,
              textAlign: 'center',
            }}>
              VENUE REPORT CARD
            </Text>
          </Pressable>

          {/* Right: dark mode + welcome + profile */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <ThemeToggleButton animated={false} />
            {user ? (
              <>
                {!isMobile && (
                  <View style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    {/* <Text style={{ color: isDarkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.5)', fontSize: 11, fontWeight: '500' }}> */}
                    <Text style={{ color: '#3D7FE6', fontSize: 11, fontWeight: '500' }}>
                      Welcome,
                    </Text>
                    {/* <Text style={{ color: isDarkMode ? '#ffffff' : '#111111', fontSize: 13, fontWeight: '700', maxWidth: 110 }} numberOfLines={1}> */}
                    <Text style={{ color: '#3D7FE6', fontSize: 13, fontWeight: '700', maxWidth: 110 }} numberOfLines={1}>
                      {userName || 'Guest'}
                    </Text>
                  </View>
                )}
                <View style={globalStyles.headerProfile}>
                  <TouchableOpacity
                    onPress={() => setMenuVisible(true)}
                    onMouseEnter={() => setMenuVisible(true)}
                    style={{ width: '100%', height: '100%' }}
                  >
                    <PersonAvatar
                      photoURL={photoURL}
                      name={profileDisplayName}
                      email={user?.email}
                      uid={user?.uid}
                      size={40}
                      style={globalStyles.headerProfileImage}
                    />
                  </TouchableOpacity>
                  {user?.verificationStatus === 'approved' && (
                    <VerifiedBadge style={[globalStyles.verifiedBadge, { backgroundColor: USER_TYPES[user.userType?.toUpperCase()]?.color || '#0bb5df' }]} />
                  )}
                </View>
              </>
            ) : (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <HamburgerIcon />
                <TouchableOpacity style={globalStyles.loginButton} onPress={handleLoginClick}>
                  <Text style={globalStyles.loginButtonText}>Login</Text>
                </TouchableOpacity>
                {!isMobile && (
                  <TouchableOpacity style={globalStyles.signupButton} onPress={handleSignUpClick}>
                    <Text style={globalStyles.signupButtonText}>Sign Up</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
          </View>
        </>
      ) : (
        <>
        {/* ── MOBILE / TABLET layout ────────────────────────────────────────── */}
        {(isMobile || isTablet) ? (
          renderCompactNavHeader(
            <Pressable onPress={() => navigation.navigate('Home')}>
              <Text
                numberOfLines={1}
                style={{
                  fontFamily: navTitleFont,
                  fontSize: 18,
                  fontWeight: '400',
                  color: navTitleColor,
                  textAlign: 'center',
                }}
              >
                VENUE REPORT CARD
              </Text>
            </Pressable>
          )
      ) : (
        showCenteredNav ? (
          /* ── DESKTOP CENTERED layout ── */
          <>
            {/* Left: nav icons right-justified toward center logo */}
            <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 20, gap: 6 }}>
                <NavBtn onPress={() => navigation.navigate('TourPlanner')} label="Map" animClass="nav-icon-map">
                <Ionicons name="map-outline" size={22} color={iconColor} />
              </NavBtn>
              <NavBtn onPress={() => navigation.navigate('MyCalendar')} label="Calendar" animClass="nav-icon-cal">
                <Ionicons name="calendar-outline" size={22} color={iconColor} />
              </NavBtn>
              {showExpenseTracker && (
                <NavBtn onPress={() => openBudget()} label="Expense Tracker" animClass="nav-icon-dollar">
                  <Ionicons name="wallet-outline" size={22} color={iconColor} />
                </NavBtn>
              )}
            </View>

            {/* Center: logo stacked above "Venue Report Card" in small Roboto */}
            <Pressable
              onPress={() => navigation.navigate('Home')}
              style={{ alignItems: 'center', flexShrink: 0 }}
            >
              <Image
                source={require('../../assets/logo.png')}
                resizeMode="contain"
                style={{ width: 52, height: 52 }}
              />
              <Text style={{
                fontFamily: 'Roboto, Arial, sans-serif',
                fontSize: 9,
                letterSpacing: 0.5,
                textTransform: 'none',
                color: isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)',
                marginTop: 2,
              }}>
                Venue Report Card
              </Text>
            </Pressable>

            {/* Right: icons left-justified toward center logo */}
            <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', paddingLeft: 20, gap: 8 }}>
              {/* Community */}
              {showCommunityNav && (
                <NavBtn onPress={handleCommunityPress} label="Community Directory" animClass="nav-icon-community">
                  <Ionicons name="people-outline" size={22} color={iconColor} />
                </NavBtn>
              )}

              {/* Messages */}
              {(
                <NavBtn
                  onPress={() => openMessages()}
                  label="Messages"
                  badge={formatUnreadCount(totalUnreadCount)}
                  animClass="nav-icon-plane"
                >
                  <Ionicons name="paper-plane-outline" size={22} color={iconColor} />
                </NavBtn>
              )}

              {/* Welcome + profile or Login/Signup — pushed to far right with spacer */}
              <View style={{ flex: 1 }} />

              {/* Dark mode toggle — right beside welcome/profile */}
              <TouchableOpacity
                onPress={toggleTheme}
                style={darkModeToggleStyle}
                accessibilityLabel={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {Platform.OS === 'web' ? (
                  <div className={`nav-icon ${isDarkMode ? 'nav-icon-sun' : 'nav-icon-moon'}`}>
                    {isDarkMode ? <SunIcon size={18} /> : <MoonIcon size={18} />}
                  </div>
                ) : (
                  isDarkMode ? <SunIcon size={18} /> : <MoonIcon size={18} />
                )}
              </TouchableOpacity>

              {user ? (
                <>
                  <View style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    {/* <Text style={{ color: isDarkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.5)', fontSize: 11, fontWeight: '500', letterSpacing: 0.2 }}> */}
                    <Text style={{ color: '#3D7FE6', fontSize: 11, fontWeight: '500', letterSpacing: 0.2 }}>
                      Welcome,
                    </Text>
                    {/* <Text style={{ color: isDarkMode ? '#ffffff' : '#111111', fontSize: 13, fontWeight: '700', maxWidth: 130 }} numberOfLines={1}> */}
                    <Text style={{ color: '#3D7FE6', fontSize: 13, fontWeight: '700', maxWidth: 130 }} numberOfLines={1}>
                      {userName || 'Guest'}
                    </Text>
                  </View>
                  <View style={globalStyles.headerProfile}>
                    <TouchableOpacity
                      onPress={() => setMenuVisible(true)}
                      onMouseEnter={() => setMenuVisible(true)}
                      style={{ width: '100%', height: '100%' }}
                    >
                      <PersonAvatar
                        photoURL={photoURL}
                        name={profileDisplayName}
                        email={user?.email}
                        uid={user?.uid}
                        size={40}
                        style={globalStyles.headerProfileImage}
                      />
                    </TouchableOpacity>
                    {user?.verificationStatus === 'approved' && (
                      <VerifiedBadge style={[
                        globalStyles.verifiedBadge,
                        { backgroundColor: USER_TYPES[user.userType?.toUpperCase()]?.color || '#0bb5df' }
                      ]} />
                    )}
                  </View>
                </>
              ) : (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <HamburgerIcon />
                  <TouchableOpacity style={globalStyles.loginButton} onPress={handleLoginClick}>
                    <Text style={globalStyles.loginButtonText}>Login</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={globalStyles.signupButton} onPress={handleSignUpClick}>
                    <Text style={globalStyles.signupButtonText}>Sign Up</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          </>
        ) : (
          /* ── DESKTOP original layout ── */
          <>
            {/* Left section: map + calendar + expense tracker */}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1 }}>
              <NavBtn onPress={() => navigation.navigate('TourPlanner')} label="Map" animClass="nav-icon-map">
                <Ionicons name="map-outline" size={22} color={iconColor} />
              </NavBtn>
              <NavBtn onPress={() => navigation.navigate('MyCalendar')} label="Calendar" animClass="nav-icon-cal">
                <Ionicons name="calendar-outline" size={22} color={iconColor} />
              </NavBtn>
              {showExpenseTracker && (
                <NavBtn onPress={() => openBudget()} label="Expense Tracker" animClass="nav-icon-dollar">
                  <Ionicons name="wallet-outline" size={22} color={iconColor} />
                </NavBtn>
              )}
            </View>

            {/* Center: logo + title */}
            <Pressable
              onPress={() => navigation.navigate('Home')}
              style={{ alignItems: 'center', flexDirection: 'row', gap: 10 }}
            >
              <Image
                source={require('../../assets/logo.png')}
                resizeMode="contain"
                style={{ width: 44, height: 44 }}
              />
              <Text style={[globalStyles.headerTitle, { fontSize: 20 }]}>
                Venue Report Card
              </Text>
            </Pressable>

            {/* Right section */}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'flex-end' }}>
              {/* Community */}
              {showCommunityNav && (
                <NavBtn onPress={handleCommunityPress} label="Community Directory" animClass="nav-icon-community">
                  <Ionicons name="people-outline" size={22} color={iconColor} />
                </NavBtn>
              )}

              {/* Messages */}
              {(
                <NavBtn
                  onPress={() => openMessages()}
                  label="Messages"
                  badge={formatUnreadCount(totalUnreadCount)}
                  animClass="nav-icon-plane"
                >
                  <Ionicons name="paper-plane-outline" size={22} color={iconColor} />
                </NavBtn>
              )}

              {/* Dark mode toggle */}
              <TouchableOpacity
                onPress={toggleTheme}
                style={darkModeToggleStyle}
                accessibilityLabel={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {Platform.OS === 'web' ? (
                  <div className={`nav-icon ${isDarkMode ? 'nav-icon-sun' : 'nav-icon-moon'}`}>
                    {isDarkMode ? <SunIcon size={18} /> : <MoonIcon size={18} />}
                  </div>
                ) : (
                  isDarkMode ? <SunIcon size={18} /> : <MoonIcon size={18} />
                )}
              </TouchableOpacity>

              {user ? (
                <>
                  {/* Welcome + username stacked, left-aligned so "Welcome" sits above-left of the name */}
                  <View style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    {/* <Text style={{ color: isDarkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.5)', fontSize: 11, fontWeight: '500', letterSpacing: 0.2 }}> */}
                    <Text style={{ color: '#3D7FE6', fontSize: 11, fontWeight: '500', letterSpacing: 0.2 }}>
                      Welcome,
                    </Text>
                    {/* <Text style={{ color: isDarkMode ? '#ffffff' : '#111111', fontSize: 13, fontWeight: '700', maxWidth: 130 }} numberOfLines={1}> */}
                    <Text style={{ color: '#3D7FE6', fontSize: 13, fontWeight: '700', maxWidth: 130 }} numberOfLines={1}>
                      {userName || 'Guest'}
                    </Text>
                  </View>
                  <View style={globalStyles.headerProfile}>
                    <TouchableOpacity
                      onPress={() => setMenuVisible(true)}
                      onMouseEnter={() => setMenuVisible(true)}
                      style={{ width: '100%', height: '100%' }}
                    >
                      <PersonAvatar
                        photoURL={photoURL}
                        name={profileDisplayName}
                        email={user?.email}
                        uid={user?.uid}
                        size={40}
                        style={globalStyles.headerProfileImage}
                      />
                    </TouchableOpacity>
                    {user?.verificationStatus === 'approved' && (
                      <VerifiedBadge style={[
                        globalStyles.verifiedBadge,
                        { backgroundColor: USER_TYPES[user.userType?.toUpperCase()]?.color || '#0bb5df' }
                      ]} />
                    )}
                  </View>
                </>
              ) : (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <HamburgerIcon />
                  <TouchableOpacity style={globalStyles.loginButton} onPress={handleLoginClick}>
                    <Text style={globalStyles.loginButtonText}>Login</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={globalStyles.signupButton} onPress={handleSignUpClick}>
                    <Text style={globalStyles.signupButtonText}>Sign Up</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          </>
        )
      )}
      </>
      )}

      {/* Community Modal */}

      {/* Sign-in prompt — shown when a logged-out user taps the Community icon */}
      <Modal
        visible={communityAuthPromptVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setCommunityAuthPromptVisible(false)}
      >
        <Pressable
          style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onPress={() => setCommunityAuthPromptVisible(false)}
        >
          <Pressable
            style={{ backgroundColor: isDarkMode ? '#1a1a2e' : '#ffffff', borderRadius: 20, width: '100%', maxWidth: 400, overflow: 'hidden' }}
            onPress={() => {}}
          >
            {/* Header */}
            <View style={{ backgroundColor: NAV_BRAND_GREEN, padding: 24, alignItems: 'center' }}>
              <Image
                source={require('../../assets/icons/group.png')}
                style={{ width: 40, height: 40, tintColor: '#fff', marginBottom: 10 }}
              />
              <Text style={{ color: '#fff', fontSize: 22, fontWeight: '800', textAlign: 'center', fontFamily: 'VastagoGrotesk' }}>
                {authPromptFeature === 'budget' ? 'Sign in to use Budget' : authPromptFeature === 'messages' ? 'Sign in to send messages' : authPromptFeature === 'map' ? 'Sign in to use the Map' : 'Join the Community'}
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginTop: 6, textAlign: 'center', lineHeight: 20 }}>
                {authPromptFeature === 'messages'
                  ? 'Messages are free with an account. Create one or sign in to message other members and your tour party.'
                  : authPromptFeature === 'budget'
                  ? 'Budget is free with an account. Create one or sign in to track your tour expenses.'
                  : authPromptFeature === 'map'
                  ? 'The Map is for members. Create an account or sign in to open it.'
                  : 'Create a free account to access the Community Directory.'}
              </Text>
            </View>

            {/* Buttons */}
            <View style={{ padding: 24, gap: 12 }}>
              <TouchableOpacity
                style={{ backgroundColor: NAV_BRAND_GREEN, borderRadius: 12, paddingVertical: 14, alignItems: 'center' }}
                onPress={() => {
                  setCommunityAuthPromptVisible(false);
                  navigation.navigate('Login', { isSignUp: true, ...authPromptReturn(authPromptFeature) });
                }}
              >
                <Text style={{ color: '#fff', fontSize: 16, fontWeight: '700' }}>Create Account</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={{ borderWidth: 1.5, borderColor: isDarkMode ? CANVA_TITLE_CREAM : NAV_BRAND_GREEN, borderRadius: 12, paddingVertical: 14, alignItems: 'center' }}
                onPress={() => {
                  setCommunityAuthPromptVisible(false);
                  navigation.navigate('Login', authPromptReturn(authPromptFeature));
                }}
              >
                <Text style={{ color: isDarkMode ? CANVA_TITLE_CREAM : NAV_BRAND_GREEN, fontSize: 16, fontWeight: '700' }}>Sign In</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={{ paddingVertical: 10, alignItems: 'center' }}
                onPress={() => setCommunityAuthPromptVisible(false)}
              >
                <Text style={{ color: isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)', fontSize: 14 }}>Maybe later</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Desktop dropdown menu */}
      <Modal
        visible={menuVisible}
        transparent={true}
        animationType="none"
        onRequestClose={() => setMenuVisible(false)}
      >
        <Pressable
          style={[globalStyles.modalOverlay, { outline: 'none' }]}
          onPress={() => setMenuVisible(false)}
          onMouseLeave={() => setMenuVisible(false)}
        >
          <Animated.View 
            style={[
              globalStyles.menuContainer,
              { 
                height: 'auto',
                maxHeight: Platform.OS === 'web' ? 'calc(100dvh - 16px)' : '92%',
                width: 360,
                marginTop: 0,
                opacity: menuAnimation,
                transform: [
                  {
                    scaleX: menuAnimation
                  },
                  {
                    scaleY: menuAnimation.interpolate({
                      inputRange: [0, 1],
                      outputRange: [0.15, 1]
                    })
                  }
                ]
              }
            ]}
          >
            <View style={[
              globalStyles.menuHeader,
              showTourCommunityHub && { justifyContent: 'space-between', paddingHorizontal: 16 },
            ]}>
              <Text style={[
                globalStyles.menuUsername,
                showTourCommunityHub && { textAlign: 'left', fontSize: 22, fontWeight: '600' },
              ]}>
                {showTourCommunityHub ? 'Admin' : (userName || 'Guest')}
              </Text>
              <View style={{ position: 'relative' }}>
                <PersonAvatar
                  photoURL={photoURL}
                  name={profileDisplayName}
                  email={user?.email}
                  uid={user?.uid}
                  size={44}
                  style={globalStyles.menuProfileImage}
                />
                {user?.verificationStatus === 'approved' && (
                  <VerifiedBadge style={[
                    globalStyles.verifiedBadge,
                    { backgroundColor: USER_TYPES[user.userType?.toUpperCase()]?.color || '#0bb5df' }
                  ]} />
                )}
              </View>
            </View>

            <ScrollView
              style={globalStyles.menuItemsScroll}
              contentContainerStyle={globalStyles.menuItems}
              keyboardShouldPersistTaps="handled"
              nestedScrollEnabled
            >
              {menuItems.map((item, index) => (
                <View key={item.id || index} style={{ width: '100%' }}>
                  <TouchableOpacity
                    style={[
                      globalStyles.menuItem,
                      { paddingVertical: 6, width: '100%' },
                      hoveredItem === index && { 
                        transform: 'translateX(-4px)',
                        transition: 'all 0.3s ease'
                      },
                    ]}
                    onPress={() => handleMenuItemPress(item)}
                    onMouseEnter={() => setHoveredItem(index)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <Text style={[
                      globalStyles.menuItemText,
                      {
                        textAlign: 'right',
                        width: '100%',
                        color: item.color || '#ffffff',
                        fontWeight: hoveredItem === index ? '600' : '500',
                      },
                    ]}>
                      {`${item.title} `}
                      {!item.live ? (
                        <Text style={{ fontSize: 11, letterSpacing: 0.4 }}>NOTIFY</Text>
                      ) : item.locked ? (
                        <Ionicons name="lock-closed-outline" size={12} color={item.color || '#ffffff'} />
                      ) : null}
                    </Text>
                  </TouchableOpacity>
                  {!item.live && notifyItemId === item.id ? (
                    <View style={{ paddingHorizontal: 16, paddingBottom: 12, gap: 8 }}>
                      <Text style={{ color: 'rgba(255,255,255,0.72)', fontSize: 12, textAlign: showTourCommunityHub ? 'right' : 'left' }}>
                        Notify me when this is live
                      </Text>
                      <TextInput
                        value={notifyEmail}
                        onChangeText={setNotifyEmail}
                        placeholder="Email address"
                        placeholderTextColor="rgba(255,255,255,0.4)"
                        keyboardType="email-address"
                        autoCapitalize="none"
                        style={{
                          borderWidth: 1,
                          borderColor: 'rgba(255,255,255,0.22)',
                          borderRadius: 8,
                          paddingHorizontal: 10,
                          paddingVertical: 8,
                          color: '#ffffff',
                          fontSize: 14,
                          backgroundColor: 'rgba(255,255,255,0.06)',
                        }}
                      />
                      <TouchableOpacity
                        onPress={() => submitNotifyForItem(item)}
                        disabled={notifySaving}
                        style={{
                          alignSelf: showTourCommunityHub ? 'flex-end' : 'flex-start',
                          backgroundColor: CANVA_MENU_BLUE,
                          borderRadius: 8,
                          paddingHorizontal: 12,
                          paddingVertical: 8,
                        }}
                      >
                        <Text style={{ color: '#ffffff', fontWeight: '700', fontSize: 13 }}>
                          {notifySaving ? 'Saving…' : 'Notify me'}
                        </Text>
                      </TouchableOpacity>
                      {notifyStatus ? (
                        <Text style={{ color: 'rgba(255,255,255,0.78)', fontSize: 12, textAlign: showTourCommunityHub ? 'right' : 'left' }}>
                          {notifyStatus}
                        </Text>
                      ) : null}
                    </View>
                  ) : null}
                </View>
              ))}
            </ScrollView>

            <TouchableOpacity
              style={[
                globalStyles.signOutButton,
                { backgroundColor: '#000000' }
              ]}
              onPress={handleSignOut}
            >
              <Text style={[globalStyles.signOutText, { color: '#ffffff' }]}>Sign Out</Text>
            </TouchableOpacity>
          </Animated.View>
        </Pressable>
      </Modal>

      {/* Mobile menu */}
      <Modal
        visible={mobileMenuVisible}
        transparent={true}
        animationType="none"
        onRequestClose={() => setMobileMenuVisible(false)}
        statusBarTranslucent={true}
      >
        <Pressable
          style={[
            globalStyles.mobileMenuOverlay,
            {
              opacity: mobileMenuAnimation.interpolate({
                inputRange: [0, 1],
                outputRange: [0, 0.5]
              })
            }
          ]}
          onPress={() => setMobileMenuVisible(false)}
        />
        <Animated.View 
          style={[
            globalStyles.mobileMenuContainer,
            {
              width: isMobile ? '86%' : '65%',
              maxWidth: 360,
              left: 0,
              right: undefined,
              opacity: mobileMenuAnimation,
              transform: [
                {
                  translateX: mobileMenuAnimation.interpolate({
                    inputRange: [0, 1],
                    outputRange: [-360, 0]
                  })
                }
              ]
            }
          ]}
        >
          <View style={globalStyles.mobileMenuHeader}>
            <TouchableOpacity
              style={[globalStyles.closeButton, { right: undefined, left: 0 }]}
              onPress={toggleTheme}
              accessibilityLabel={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <Ionicons
                name={isDarkMode ? 'sunny-outline' : 'moon-outline'}
                size={18}
                color="#ffffff"
              />
            </TouchableOpacity>
            <TouchableOpacity 
              style={globalStyles.closeButton}
              onPress={() => setMobileMenuVisible(false)}
            >
              <Text style={globalStyles.closeButtonText}>✕</Text>
            </TouchableOpacity>
            {user ? (
              <TouchableOpacity
                style={globalStyles.mobileProfileSection}
                onPress={() => {
                  const accountItem = menuItems.find((item) => item.id === 'account');
                  if (accountItem) handleMenuItemPress(accountItem);
                  else {
                    setMobileMenuVisible(false);
                    navigation.navigate('Profile');
                  }
                }}
                accessibilityLabel="Account"
              >
                <PersonAvatar
                  photoURL={photoURL}
                  name={profileDisplayName}
                  email={user?.email}
                  uid={user?.uid}
                  size={56}
                  style={globalStyles.mobileProfileImage}
                />
                <Text style={globalStyles.mobileUsername}>{userName || 'Guest'}</Text>
              </TouchableOpacity>
            ) : (
              <View style={globalStyles.mobileProfileSection}>
                <Text style={globalStyles.mobileUsername}>Guest</Text>
              </View>
            )}
          </View>

          <ScrollView
            style={globalStyles.mobileMenuItems}
            keyboardShouldPersistTaps="handled"
            nestedScrollEnabled
          >
            {menuItems.map((item, index) => (
              <View key={item.id || index}>
                <TouchableOpacity
                  style={globalStyles.mobileMenuItem}
                  onPress={() => handleMenuItemPress(item)}
                >
                  <Text style={[
                    globalStyles.mobileMenuItemText,
                    { color: item.color || '#ffffff' },
                  ]}>{item.live ? item.title : `${item.title}  ·  Notify`}</Text>
                </TouchableOpacity>
                {!item.live && notifyItemId === item.id ? (
                  <View style={{ paddingHorizontal: 20, paddingBottom: 12, gap: 8 }}>
                    <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>Notify me when this is live</Text>
                    <TextInput
                      value={notifyEmail}
                      onChangeText={setNotifyEmail}
                      placeholder="Email address"
                      placeholderTextColor="rgba(255,255,255,0.4)"
                      keyboardType="email-address"
                      autoCapitalize="none"
                      style={{
                        borderWidth: 1,
                        borderColor: 'rgba(255,255,255,0.22)',
                        borderRadius: 8,
                        paddingHorizontal: 10,
                        paddingVertical: 8,
                        color: '#ffffff',
                        fontSize: 14,
                      }}
                    />
                    <TouchableOpacity onPress={() => submitNotifyForItem(item)} disabled={notifySaving}>
                      <Text style={{ color: CANVA_MENU_BLUE, fontWeight: '700' }}>{notifySaving ? 'Saving…' : 'Notify me'}</Text>
                    </TouchableOpacity>
                    {notifyStatus ? <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>{notifyStatus}</Text> : null}
                  </View>
                ) : null}
              </View>
            ))}
          </ScrollView>
            
            {user ? (
              <TouchableOpacity
                style={[
                  globalStyles.mobileSignOutButton,
                  { backgroundColor: '#000000' }
                ]}
                onPress={() => {
                  setMobileMenuVisible(false);
                  handleSignOut();
                }}
              >
                <Text style={[globalStyles.mobileSignOutText, { color: '#ffffff' }]}>Sign Out</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={[
                  globalStyles.mobileSignOutButton,
                  { backgroundColor: '#0bb5df' }
                ]}
                onPress={() => {
                  setMobileMenuVisible(false);
                  handleLoginClick();
                }}
              >
                <Text style={[globalStyles.mobileSignOutText, { color: '#ffffff' }]}>Login</Text>
              </TouchableOpacity>
            )}
            
            {!user && (
              <TouchableOpacity
                style={[
                  globalStyles.mobileSignOutButton,
                  { 
                    backgroundColor: 'transparent',
                    borderWidth: 1,
                    borderColor: '#0bb5df',
                    marginTop: 10
                  }
                ]}
                onPress={() => {
                  setMobileMenuVisible(false);
                  handleSignUpClick();
                }}
              >
                <Text style={[globalStyles.mobileSignOutText, { color: '#0bb5df' }]}>Sign Up</Text>
              </TouchableOpacity>
            )}
        </Animated.View>
      </Modal>

      <GradeVenueModal
        visible={gradeModalVisible}
        onClose={() => setGradeModalVisible(false)}
      />

      {/* Messages / Community Sidebar */}
      <CommunityModal />
    </HideOnScroll>
  );
}

// Nav icon button style (used for all icon buttons in new navbar)
const navBtnStyle = (isDark) => ({
  position: 'relative',
  width: 40,
  height: 40,
  borderRadius: 10,
  backgroundColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'background 0.15s ease',
});

// Header Unread Badge Styles (for header bell icon)
const headerUnreadBadgeStyle = {
  position: 'absolute',
  top: -8,
  right: -8,
  backgroundColor: '#e74c3c',
  borderRadius: 10,
  minWidth: 20,
  height: 20,
  alignItems: 'center',
  justifyContent: 'center',
  paddingHorizontal: 6,
  borderWidth: 2,
  borderColor: '#ffffff',
};

const headerUnreadTextStyle = {
  color: '#ffffff',
  fontSize: 11,
  fontFamily: 'VastagoGrotesk',
  fontWeight: 'bold',
};

const darkModeToggleStyle = {
  width: 36,
  height: 36,
  borderRadius: 18,
  backgroundColor: 'rgba(0, 0, 0, 0.05)',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: 8,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
};